import React, { useState } from "react";
import { Box, CircularProgress, Fab, Tooltip, Typography } from "@mui/material";
import styles from "./SearchTermPanel.module.css";
import GenericInputField from "../GenericInputField";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { globalBg } from "../../styles/theme";
import { generateSearchTerms } from "../../services/fetch";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import { useConfig } from "../../context/ConfigContext";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import { ScraperConfigOptionValue } from "../TopicsPanel/types";

const SearchTermPanel = ({}) => {
	const { selectedClient } = useClientAccessContext();
	const { generatedSearchTerms, setGeneratedSearchTerms } =
		useAdminPanelContext();
	const { selectedTopic } = useConfig();
	const [value, setValue] = useState("");
	const [loading, setLoading] = useState(false);

	const handleGenerateSearchTerms = async () => {
		setLoading(true);
		const response = await generateSearchTerms(
			selectedClient.clientName,
			selectedTopic.name,
			value
		);
		setGeneratedSearchTerms(response?.result?.data);
		setLoading(false);
	};

	return (
		<Box className={styles.saerchTermPanelContainer}>
			<Box className={styles.row}>
				<Typography variant="h5" sx={{ color: "white" }}>
					Search term wizard
				</Typography>
			</Box>
			<Box className={styles.row}>
				<Typography color="white">Wizard prompt</Typography>
				<Box sx={{ background: "white" }}>
					<GenericInputField
						placeholder=""
						multiline
						currentValue={value}
						onValueChange={setValue}
					/>
				</Box>
			</Box>
			<Box>
				<Box sx={{ position: "relative" }}>
					<Fab
						disabled={!value.length}
						aria-label="save"
						sx={{ color: "black" }}
						onClick={handleGenerateSearchTerms}
					>
						<AutoFixHighIcon />
					</Fab>
					{loading && (
						<CircularProgress
							size={68}
							sx={{
								color: globalBg,
								position: "absolute",
								top: -6,
								left: -6,
								zIndex: 1,
							}}
						/>
					)}
				</Box>
			</Box>
			{generatedSearchTerms.length ? (
				<Box className={styles.row}>
					<Typography color="white">Search terms</Typography>
					<Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
						{generatedSearchTerms.map((searchTerm) => (
							<SearchTermItem key={searchTerm} searchTerm={searchTerm} />
						))}
					</Box>
				</Box>
			) : null}
		</Box>
	);
};

const SearchTermItem = ({ searchTerm }: { searchTerm: string }) => {
	const {
		handleConfigOptionValues,
		selectedConfigId,
		topics,
		selectedTopic,
		setGeneratedSearchTerms,
	} = useAdminPanelContext();

	const handleAdd = () => {
		const currentLowestId = getLowestConfigOptionValueId(selectedConfigId);

		const unsavedItem: ScraperConfigOptionValue = {
			id: !currentLowestId || currentLowestId > 0 ? -1 : currentLowestId - 1,
			active: true,
			value: searchTerm,
		};
		handleConfigOptionValues.add(
			selectedConfigId,
			"searchterm",
			unsavedItem,
			false
		);
		handleDelete();
	};

	const handleDelete = () => {
		setGeneratedSearchTerms((prev) =>
			prev.filter((term) => term !== searchTerm)
		);
	};

	const getLowestConfigOptionValueId = (configId: number) => {
		if (!topics || selectedTopic === null) {
			return null;
		}

		let lowestId = null;

		topics.forEach((topic) => {
			topic.scraperConfigs.forEach((config) => {
				if (config.id === configId) {
					config.scraperConfigOptions.forEach((option) => {
						if (option.name === "searchterm") {
							option.scraperConfigOptionValues.forEach((value) => {
								if (value.id < lowestId) {
									lowestId = value.id;
								}
							});
						}
					});
				}
			});
		});
		return lowestId;
	};

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
			<Box
				onClick={handleAdd}
				sx={{
					backgroundColor: "#d6d6de",
					height: "40px",
					width: "40px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderStartStartRadius: "8px",
					borderEndStartRadius: "8px",
					"&:hover": {
						cursor: "pointer",
					},
				}}
			>
				<ArrowBackIcon />
			</Box>
			<Box
				onClick={handleDelete}
				sx={{
					backgroundColor: "#d6d6de",
					height: "40px",
					width: "40px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					"&:hover": {
						cursor: "pointer",
					},
				}}
			>
				<ClearIcon />
			</Box>
			<Tooltip title={searchTerm} placement="top" arrow>
				<Box
					sx={{
						backgroundColor: "#d6d6de",
						width: "80%",
						height: "40px",
						display: "flex",
						alignItems: "center",
						paddingInline: "10px",
						overflow: "hidden",
						maxWidth: "296px",
					}}
				>
					<Typography
						sx={{
							color: "black",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{searchTerm}
					</Typography>
				</Box>
			</Tooltip>
		</Box>
	);
};

export default SearchTermPanel;
